import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  CircularProgress, FormControlLabel, IconButton, Switch, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import UploadFileIcon   from '@mui/icons-material/UploadFile';
import DownloadIcon     from '@mui/icons-material/Download';
import DescriptionIcon  from '@mui/icons-material/Description';
import { AppDispatch } from '../../reduxStore/Store';
import { fetchModeleWordVariables, uploadModeleWord } from '../../reduxStore/ReportTemplateSlice';
import { ModeleWordGroupe, ModeleWordRef, ModeleWordVariable } from '../../models/models';
import { usePermission } from '../../hooks/usePermission';
import { useSnackbar } from '../SnackbarProvider';
import {
  messageErreurTelechargement, telechargerModeleDepart, telechargerModeleWordActuel,
} from '../../utils/modeleWordFile';

interface Props {
  /** Absent tant que le template n'est pas enregistré (pas d'upload possible). */
  templateId?: number;
  modeleWord?: ModeleWordRef;
  onChange: (modeleWord: ModeleWordRef) => void;
}

const mono = { fontFamily: 'monospace', fontSize: 13 };

/** Bloc d'exemple : ce que le client tape dans Word, ligne par ligne. */
const Exemple: React.FC<{ titre: string; lignes: string[]; note: string }> = ({ titre, lignes, note }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" fontWeight={600}>{titre}</Typography>
    <Box sx={{ ...mono, bgcolor: '#f4f5f7', border: '1px solid #e4e7ec', borderRadius: 1, p: 1, my: 0.5, whiteSpace: 'pre-wrap' }}>
      {lignes.join('\n')}
    </Box>
    <Typography variant="caption" color="text.secondary">{note}</Typography>
  </Box>
);

const ModeleWordSection: React.FC<Props> = ({ templateId, modeleWord, onChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const variables = useSelector((state: any) => state.ReportTemplateSlice.modeleWordVariables) as ModeleWordGroupe[];
  const uploading = useSelector((state: any) => state.ReportTemplateSlice.modeleWordUploading) as boolean;
  const peutEnvoyer     = usePermission('options.update');
  const peutTelecharger = usePermission('options.read');
  const inputRef = useRef<HTMLInputElement>(null);

  // Résultat du dernier envoi, gardé affiché (un message de refus peut être long)
  const [refus,          setRefus]          = useState<string | null>(null);
  const [avertissements, setAvertissements] = useState<string[]>([]);

  useEffect(() => {
    if (variables.length === 0) dispatch(fetchModeleWordVariables());
  }, [dispatch, variables.length]);

  const aUnFichier = !!modeleWord?.fichier_ref;

  const copier = (texte: string) => {
    navigator.clipboard.writeText(texte)
      .then(() => showSnackbar(`${texte} copié : collez-le dans Word`, 'success'))
      .catch(() => showSnackbar('Copie impossible', 'error'));
  };

  const envoyer = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0];
    e.target.value = '';   // permet de renvoyer le même fichier après correction
    if (!fichier || !templateId) return;
    setRefus(null);
    setAvertissements([]);
    const result = await dispatch(uploadModeleWord({ id: templateId, fichier }));
    if (uploadModeleWord.fulfilled.match(result)) {
      onChange(result.payload.modele_word);
      setAvertissements(result.payload.avertissements);
      showSnackbar('Modèle Word enregistré', 'success');
    } else {
      setRefus(result.payload as string);
    }
  };

  const telecharger = async (action: () => Promise<void>) => {
    try { await action(); }
    catch (err) { showSnackbar(await messageErreurTelechargement(err), 'error'); }
  };

  const balise = (v: ModeleWordVariable) => (
    <Box key={v.nom} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
      <Chip label={`{${v.nom}}`} size="small" sx={{ ...mono, minWidth: 150, justifyContent: 'flex-start' }} />
      <Tooltip title="Copier la balise">
        <IconButton size="small" onClick={() => copier(`{${v.nom}}`)}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton>
      </Tooltip>
      <Typography variant="body2" sx={{ flex: 1 }}>{v.libelle}</Typography>
      {v.exemple && <Typography variant="caption" color="text.secondary">ex : {v.exemple}</Typography>}
    </Box>
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Imprimez vos documents avec votre propre mise en page Word. Le fichier est vérifié à l'envoi ;
        un seul modèle par template, un nouvel envoi remplace le précédent.
      </Typography>

      {/* État du modèle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <DescriptionIcon sx={{ color: aUnFichier ? '#2563eb' : '#667085' }} />
        {aUnFichier ? (
          <Typography variant="body2">
            Modèle actuel : <strong>{modeleWord!.fichier_nom}</strong>
            {modeleWord!.importe_le && ` — envoyé le ${new Date(modeleWord!.importe_le).toLocaleString('fr-FR')}`}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Aucun modèle Word : l'impression utilise la mise en page standard.
          </Typography>
        )}
        {aUnFichier && peutTelecharger && templateId && (
          <Button size="small" startIcon={<DownloadIcon />}
            onClick={() => telecharger(() => telechargerModeleWordActuel(templateId, modeleWord!.fichier_nom ?? 'modele.docx'))}>
            Télécharger
          </Button>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => telecharger(telechargerModeleDepart)}>
          Télécharger le modèle de départ
        </Button>
        {peutEnvoyer && (
          <>
            <Button
              variant="contained" size="small"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              disabled={!templateId || uploading}
              onClick={() => inputRef.current?.click()}
            >
              {aUnFichier ? 'Remplacer le modèle' : 'Envoyer mon modèle (.docx)'}
            </Button>
            <input ref={inputRef} type="file" hidden onChange={envoyer}
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
          </>
        )}
      </Box>

      {!templateId && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Enregistrez d'abord le template, puis rouvrez-le pour envoyer votre modèle Word.
        </Alert>
      )}
      {refus && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setRefus(null)}>{refus}</Alert>}
      {avertissements.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setAvertissements([])}>
          Modèle enregistré, mais à vérifier :
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {avertissements.map(a => <li key={a}>{a}</li>)}
          </Box>
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={!!modeleWord?.actif && aUnFichier}
            disabled={!aUnFichier}
            onChange={e => onChange({ ...(modeleWord ?? {}), actif: e.target.checked })}
          />
        }
        label="Utiliser le modèle Word pour imprimer les documents"
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
        Pris en compte à l'enregistrement du template. Désactivé : la mise en page standard est utilisée.
      </Typography>

      {/* Guide */}
      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Comment faire</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <li>Téléchargez le <strong>modèle de départ</strong> et ouvrez-le dans Word.</li>
            <li>Ajoutez votre logo (Insertion → Image), vos coordonnées, vos couleurs et vos mentions.</li>
            <li>Placez les <strong>balises</strong> <Box component="span" sx={mono}>{'{…}'}</Box> là où les valeurs doivent apparaître : copiez-les depuis la liste « Variables disponibles ».</li>
            <li>Enregistrez au format <strong>Document Word (.docx)</strong> et envoyez-le. Il est vérifié immédiatement : une erreur est expliquée et rien n'est enregistré.</li>
            <li>
              Faites un essai sans gêner les impressions en cours : activez le modèle Word sur un template qui n'est
              <strong> pas</strong> « Défaut », imprimez un document en choisissant ce template dans le menu d'impression,
              puis cochez « Défaut » quand le résultat vous convient.
            </li>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Variables disponibles</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {variables.length === 0 && <CircularProgress size={20} />}
          {variables.map(g => (
            <Box key={g.groupe} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#2563eb', mb: 0.5 }}>{g.groupe}</Typography>
              {(g.variables ?? []).map(balise)}
              {g.boucle && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                    <Chip label={`{#${g.boucle.nom}} … {/${g.boucle.nom}}`} size="small" color="primary" variant="outlined" sx={mono} />
                    <Typography variant="body2">{g.boucle.libelle}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>{g.boucle.aide}</Typography>
                  <Box sx={{ pl: 2, borderLeft: '2px solid #eef4ff' }}>{g.boucle.champs.map(balise)}</Box>
                </>
              )}
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Exemples</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Exemple
            titre="Répéter une ligne de tableau pour chaque article"
            lignes={['| {#lignes}{code} | {designation} | {quantite} | {total_ht}{/lignes} |']}
            note="{#lignes} dans la première cellule, {/lignes} dans la dernière, sur la même ligne du tableau. Gardez une ligne d'en-tête au-dessus : elle sera répétée en haut de chaque page si vous cochez « Répéter en haut de chaque page » dans les propriétés du tableau."
          />
          <Exemple
            titre="Afficher une ligne seulement si elle est renseignée"
            lignes={['{#reference}', 'Réf. : {reference}', '{/reference}']}
            note="{#reference} et {/reference} chacun seul sur sa ligne : si la référence est vide, la ligne disparaît entièrement. Sur une seule ligne ({#reference}Réf. : {reference}{/reference}), il resterait une ligne blanche."
          />
          <Exemple
            titre="Ventilation de la TVA"
            lignes={['{#tva}', 'TVA {taux} : {montant}', '{/tva}']}
            note="Une ligne par taux de TVA présent dans le document."
          />
          <Exemple
            titre="Numéro de page"
            lignes={['Page [champ PAGE] sur [champ NUMPAGES]']}
            note="Pas de balise : utilisez Insertion → Numéro de page dans le pied de page de Word. Le modèle de départ l'a déjà."
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Bonnes pratiques et erreurs fréquentes</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <li><strong>Collez les balises</strong> depuis la liste plutôt que de les taper : une faute de frappe s'imprime vide. Si vous les tapez, faites-le d'un seul trait, sans changer la mise en forme au milieu.</li>
            <li><strong>Polices</strong> : Arial, Calibri, Times New Roman ou Courier New. Une autre police risque d'être remplacée et de décaler le texte.</li>
            <li><strong>Mise en page</strong> : placez les blocs avec des tableaux (bordures invisibles si besoin). Évitez les zones de texte et formes flottantes, qui peuvent se déplacer.</li>
            <li><strong>Images</strong> : insérez-les dans le document (Insertion → Image). Une image liée à un fichier ou à un site est refusée.</li>
            <li><strong>« La section {'{#tva}'} n'est pas fermée »</strong> : il manque {'{/tva}'} après la ligne à répéter.</li>
            <li><strong>« La balise {'{numero'} n'est pas fermée »</strong> : il manque l'accolade fermante {'}'}.</li>
            <li><strong>« La balise {'{designation}'} n'est utilisable qu'entre {'{#lignes}'} et {'{/lignes}'} »</strong> : déplacez-la dans la ligne du tableau des articles.</li>
            <li><strong>Macros refusées</strong> : enregistrez en « Document Word (.docx) », pas en .docm ni .doc.</li>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ModeleWordSection;
